import { useCallback, useEffect, useRef, useState } from 'react'

// Gamepad (Gamepad API, Standard-Mapping, z. B. Bluetooth-Controller am iPad/Safari) für das Cockpit.
// Modus 2 wie DJI-Standard: linker Stick = steigen/sinken (Y) + drehen (X), rechter Stick = vor/zurück (Y) + seitlich (X).
// Taste B/Kreis (Standard-Mapping: Index 1) löst den Notstopp aus (nur an der steigenden Flanke, nicht dauernd).
// Safari meldet ein Gamepad erst nach der ersten Tastenberührung – deshalb wird zusätzlich zyklisch nachgesehen.

export interface StickWerte { x: number; y: number; z: number; r: number }

export const TOTZONE = 0.1
const NOTSTOPP_TASTE = 1

/** Totzone abziehen und den Rest auf 0..1 strecken (kein Sprung am Rand der Totzone). */
export function mitTotzone(v: number, tz = TOTZONE) {
  const a = Math.abs(v)
  if (a <= tz) return 0
  return Math.sign(v) * Math.min(1, (a - tz) / (1 - tz))
}

export function useGamepad({ aktiv, onNotstopp, onGetrennt }: {
  aktiv: boolean
  onNotstopp?: () => void
  onGetrennt?: () => void
}) {
  const [name, setName] = useState<string | null>(null)
  const index = useRef<number | null>(null)
  const notstoppHalten = useRef(false)
  const cbNotstopp = useRef(onNotstopp)
  const cbGetrennt = useRef(onGetrennt)
  cbNotstopp.current = onNotstopp
  cbGetrennt.current = onGetrennt

  const finden = useCallback((): Gamepad | null => {
    const liste = typeof navigator !== 'undefined' && navigator.getGamepads ? Array.from(navigator.getGamepads()) : []
    const alle = liste.filter((g): g is Gamepad => !!g && g.connected)
    return alle.find(g => g.index === index.current) ?? alle.find(g => g.mapping === 'standard') ?? alle[0] ?? null
  }, [])

  // Erkennung + B-Taste: alle 50 ms nachsehen (Ereignisse allein sind bei Safari unzuverlässig)
  useEffect(() => {
    if (!aktiv) return
    const takt = setInterval(() => {
      const g = finden()
      if (g) {
        if (index.current !== g.index) { index.current = g.index; setName(g.id) }
        const gedrueckt = !!g.buttons[NOTSTOPP_TASTE]?.pressed
        if (gedrueckt && !notstoppHalten.current) cbNotstopp.current?.()
        notstoppHalten.current = gedrueckt
      } else if (index.current !== null) {
        index.current = null; notstoppHalten.current = false; setName(null)
        cbGetrennt.current?.()
      }
    }, 50)
    return () => clearInterval(takt)
  }, [aktiv, finden])

  /** Aktuelle Stickwerte (Totzone angewandt); null, wenn kein Gamepad verbunden ist. */
  const lesen = useCallback((): StickWerte | null => {
    const g = finden()
    if (!g) return null
    const a = (i: number) => g.axes[i] ?? 0
    return {
      r: mitTotzone(a(0)),    // links X: drehen
      z: -mitTotzone(a(1)),   // links Y: steigen (Stick nach oben = Achse negativ)
      x: mitTotzone(a(2)),    // rechts X: seitlich
      y: -mitTotzone(a(3)),   // rechts Y: vor
    }
  }, [finden])

  return { verbunden: name !== null, name, lesen }
}
